import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Skeleton,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Calendar,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Gauge,
  Lightbulb,
  MapPin,
  Plus,
  RefreshCw,
  Settings2,
  Ship,
  Sparkles,
  Sun,
  Target,
  Trash2,
  TrendingDown,
  X,
  Zap,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDialog } from '../../components/confirm-dialog';
import { LoadingState } from '../../components/loading-state';
import { PriceChangeBadge } from '../../components/price-change-badge';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import {
  type CompSet,
  compSetsApi,
  type LocalEvent,
  localEventsApi,
  type PricingStrategy,
  type PricingSuggestion,
  pricingSuggestionsApi,
} from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/dynamic-pricing')({
  component: DynamicPricingPage,
});

// ── Strategy Definitions ──

interface StrategyDef {
  type: string;
  label: string;
  icon: typeof BarChart3;
  color: { bg: string; text: string };
  shortDesc: string;
  longDesc: string;
  example: string;
  requiresCompSet: boolean;
  params: StrategyParamDef[];
}

interface StrategyParamDef {
  key: string;
  label: string;
  unit: string;
  defaultValue: number;
  min: number;
  max: number;
  step: number;
  hint: string;
}

const STRATEGY_DEFS: StrategyDef[] = [
  {
    type: 'undercut_pct',
    label: 'Undercut by %',
    icon: TrendingDown,
    color: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
    shortDesc: 'Price below the competition',
    longDesc:
      "Automatically suggests prices that are a fixed percentage below your competitor set's average price. Great for gaining market share by being consistently cheaper than similar boats.",
    example: 'If competitors average \u20AC2,000/week and you set 5%, it suggests \u20AC1,900.',
    requiresCompSet: true,
    params: [
      {
        key: 'pct',
        label: 'Undercut percentage',
        unit: '%',
        defaultValue: 5,
        min: 1,
        max: 50,
        step: 1,
        hint: 'How much cheaper than the competitor average',
      },
    ],
  },
  {
    type: 'match_comp_avg',
    label: 'Match Comp Average',
    icon: BarChart3,
    color: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
    shortDesc: 'Stay at the market rate',
    longDesc:
      'Keeps your pricing aligned with the average price across your competitor set. Use this when you want to stay competitive without being the cheapest \u2014 ideal if your boat has strong reviews or unique features.',
    example: 'If competitors average \u20AC2,000/week, it suggests \u20AC2,000.',
    requiresCompSet: true,
    params: [],
  },
  {
    type: 'target_margin',
    label: 'Target Margin',
    icon: Target,
    color: {
      bg: 'bg-violet-100 dark:bg-violet-900/30',
      text: 'text-violet-600 dark:text-violet-400',
    },
    shortDesc: 'Markup over your cost base',
    longDesc:
      'Adds a fixed profit margin on top of your list (base) price. Best when you know your costs and want to guarantee a minimum profit percentage regardless of what competitors charge.',
    example: 'If your list price is \u20AC1,600 and margin is 25%, it suggests \u20AC2,000.',
    requiresCompSet: false,
    params: [
      {
        key: 'marginPct',
        label: 'Target margin',
        unit: '%',
        defaultValue: 25,
        min: 5,
        max: 100,
        step: 5,
        hint: 'Profit margin on top of list price',
      },
    ],
  },
  {
    type: 'early_bird',
    label: 'Early Bird',
    icon: Clock,
    color: { bg: 'bg-sky-100 dark:bg-sky-900/30', text: 'text-sky-600 dark:text-sky-400' },
    shortDesc: 'Discount for advance bookings',
    longDesc:
      'Offers a discount for charter periods that are far in the future. Encourages customers to book well in advance, helping you secure revenue early and plan crew & logistics ahead of time.',
    example: 'A 15% discount for bookings 12+ weeks out turns \u20AC2,000 into \u20AC1,700.',
    requiresCompSet: false,
    params: [
      {
        key: 'discountPct',
        label: 'Discount',
        unit: '%',
        defaultValue: 15,
        min: 5,
        max: 40,
        step: 5,
        hint: 'How much to reduce the current price',
      },
      {
        key: 'weeksOut',
        label: 'Minimum weeks out',
        unit: 'weeks',
        defaultValue: 12,
        min: 4,
        max: 52,
        step: 1,
        hint: 'Only applies when the charter is this far away',
      },
    ],
  },
  {
    type: 'last_minute',
    label: 'Last Minute',
    icon: Zap,
    color: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-600 dark:text-orange-400',
    },
    shortDesc: 'Fill empty slots fast',
    longDesc:
      'Suggests a steep discount for charter periods that are coming up soon and remain unbooked. Helps you avoid empty weeks by lowering the price as the departure date approaches.',
    example: 'A 20% discount within 14 days turns \u20AC2,000 into \u20AC1,600.',
    requiresCompSet: false,
    params: [
      {
        key: 'discountPct',
        label: 'Discount',
        unit: '%',
        defaultValue: 20,
        min: 5,
        max: 50,
        step: 5,
        hint: 'How much to reduce the current price',
      },
      {
        key: 'daysOut',
        label: 'Days before departure',
        unit: 'days',
        defaultValue: 14,
        min: 3,
        max: 60,
        step: 1,
        hint: 'Applies when the charter starts within this many days',
      },
    ],
  },
  {
    type: 'demand_based',
    label: 'Demand-Based',
    icon: Gauge,
    color: { bg: 'bg-rose-100 dark:bg-rose-900/30', text: 'text-rose-600 dark:text-rose-400' },
    shortDesc: 'Adjust by occupancy',
    longDesc:
      'Automatically raises or lowers prices based on fleet occupancy. When demand is high (many boats booked), prices go up. When demand is low and departure is imminent, prices drop to fill empty slots.',
    example: 'At 80% occupancy, raise by 15%. At 20% occupancy within 14 days, drop by 20%.',
    requiresCompSet: false,
    params: [
      {
        key: 'highDemandPct',
        label: 'High demand markup',
        unit: '%',
        defaultValue: 15,
        min: 5,
        max: 50,
        step: 5,
        hint: 'Price increase when occupancy exceeds 70%',
      },
      {
        key: 'lowDemandPct',
        label: 'Low demand discount',
        unit: '%',
        defaultValue: 20,
        min: 5,
        max: 50,
        step: 5,
        hint: 'Price decrease when occupancy is below 30% and within 14 days',
      },
    ],
  },
  {
    type: 'seasonal_multiplier',
    label: 'Seasonal Multiplier',
    icon: Sun,
    color: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-600 dark:text-amber-400' },
    shortDesc: 'Season-based pricing',
    longDesc:
      'Applies different price multipliers based on the Mediterranean charter season tier. Peak summer weeks get a premium, while off-season and pre-season get discounts to match demand patterns.',
    example: 'Peak (Jul-Aug): +15%, Shoulder: -10%, Pre-season: -35%.',
    requiresCompSet: false,
    params: [
      {
        key: 'peakMult',
        label: 'Peak multiplier',
        unit: '×',
        defaultValue: 1.15,
        min: 1.0,
        max: 2.0,
        step: 0.05,
        hint: 'Jul-Aug multiplier (1.15 = +15%)',
      },
      {
        key: 'shoulderMult',
        label: 'Shoulder multiplier',
        unit: '×',
        defaultValue: 0.9,
        min: 0.5,
        max: 1.0,
        step: 0.05,
        hint: 'Jun, Sep multiplier (0.90 = -10%)',
      },
      {
        key: 'preMult',
        label: 'Pre-season multiplier',
        unit: '×',
        defaultValue: 0.65,
        min: 0.3,
        max: 1.0,
        step: 0.05,
        hint: 'Apr-May, Oct multiplier',
      },
    ],
  },
  {
    type: 'day_of_week',
    label: 'Day of Week',
    icon: Calendar,
    color: {
      bg: 'bg-indigo-100 dark:bg-indigo-900/30',
      text: 'text-indigo-600 dark:text-indigo-400',
    },
    shortDesc: 'Weekend premium / midweek discount',
    longDesc:
      'Adjusts prices based on departure day. Saturday departures command a premium since most charter guests prefer weekend starts. Midweek departures get a discount to incentivize flexible travelers.',
    example: 'Saturday departure: +10%. Wednesday departure: -5%.',
    requiresCompSet: false,
    params: [
      {
        key: 'weekendPremiumPct',
        label: 'Weekend premium',
        unit: '%',
        defaultValue: 10,
        min: 0,
        max: 30,
        step: 5,
        hint: 'Extra charge for Saturday departures',
      },
      {
        key: 'midweekDiscountPct',
        label: 'Midweek discount',
        unit: '%',
        defaultValue: 5,
        min: 0,
        max: 30,
        step: 5,
        hint: 'Discount for Monday-Thursday departures',
      },
    ],
  },
  {
    type: 'event_surge',
    label: 'Event Surge',
    icon: Sparkles,
    color: {
      bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/30',
      text: 'text-fuchsia-600 dark:text-fuchsia-400',
    },
    shortDesc: 'Surge pricing for local events',
    longDesc:
      "Increases prices when charter dates overlap with local events you've added (regattas, festivals, concerts). Events drive up demand in the area, letting you capture premium pricing.",
    example: 'A regatta in Split means +20% for overlapping charters.',
    requiresCompSet: false,
    params: [
      {
        key: 'surgePct',
        label: 'Surge markup',
        unit: '%',
        defaultValue: 20,
        min: 5,
        max: 50,
        step: 5,
        hint: 'Price increase when charter overlaps a local event',
      },
    ],
  },
];

const STRATEGY_LABELS: Record<string, string> = {
  ...Object.fromEntries(STRATEGY_DEFS.map((d) => [d.type, d.label])),
  last_minute_deal: 'Last-Minute Deal',
  orphan_gap: 'Fillable Gap',
};

// ── Page ──

function DynamicPricingPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <DynamicPricingContent orgPid={organization.pid} />;
}

function DynamicPricingContent({ orgPid }: { orgPid: string }) {
  const { data: suggestionsData } = useQuery({
    queryKey: queryKeys.suggestions.list(orgPid, 1, '', 'pending'),
    queryFn: () => pricingSuggestionsApi.listSuggestions({ status: 'pending', limit: 1 }),
    staleTime: 30_000,
  });

  const pendingCount = suggestionsData?.meta.total ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Dynamic Pricing</h1>
        <p className="text-muted-foreground">Configure strategies and review pricing suggestions</p>
      </div>

      <Tabs defaultValue="suggestions">
        <TabsList>
          <TabsTrigger value="suggestions">
            Suggestions{pendingCount > 0 ? ` (${pendingCount})` : ''}
          </TabsTrigger>
          <TabsTrigger value="strategies">Strategies</TabsTrigger>
          <TabsTrigger value="events">Events</TabsTrigger>
        </TabsList>

        <TabsContent value="suggestions" className="mt-4">
          <SuggestionsTab orgPid={orgPid} />
        </TabsContent>

        <TabsContent value="strategies" className="mt-4">
          <StrategiesTab orgPid={orgPid} />
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <EventsTab orgPid={orgPid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Strategies Tab ──

function StrategiesTab({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PricingStrategy | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.strategies.list(orgPid),
    queryFn: () => pricingSuggestionsApi.listStrategies({ limit: 100 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (pid: string) => pricingSuggestionsApi.removeStrategy(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.strategies.list(orgPid) });
      toast.success('Strategy deleted');
    },
    onError: () => toast.error('Failed to delete strategy'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ pid, isActive }: { pid: string; isActive: boolean }) =>
      pricingSuggestionsApi.updateStrategy(pid, { isActive }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.strategies.list(orgPid) });
    },
  });

  const strategies = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">
          {strategies.length} {strategies.length === 1 ? 'strategy' : 'strategies'} configured
        </p>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-1 size-4" />
              New Strategy
            </Button>
          </DialogTrigger>
          <CreateStrategyDialog
            orgPid={orgPid}
            onSuccess={() => {
              setCreateOpen(false);
              void queryClient.invalidateQueries({ queryKey: queryKeys.strategies.list(orgPid) });
            }}
          />
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : strategies.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Settings2 className="mx-auto mb-2 size-8 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground font-medium">No strategies yet</p>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto text-xs">
              Pricing strategies define rules for automatic pricing suggestions. Create one to start
              optimizing your charter rates based on market data and booking patterns.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {strategies.map((strategy) => {
            const def = STRATEGY_DEFS.find((d) => d.type === strategy.strategyType);
            const Icon = def?.icon ?? Settings2;

            return (
              <Card key={strategy.id}>
                <CardContent className="flex items-center gap-4 p-4">
                  <div
                    className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${def?.color.bg ?? 'bg-muted'}`}
                  >
                    <Icon className={`size-5 ${def?.color.text ?? 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{strategy.name}</p>
                      <Badge variant="outline" className="text-xs">
                        {STRATEGY_LABELS[strategy.strategyType] ?? strategy.strategyType}
                      </Badge>
                      {strategy.priority > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          Priority {strategy.priority}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-xs">
                      {formatStrategyParams(strategy.strategyType, strategy.params)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Scope:{' '}
                      {strategy.compSetId
                        ? 'Competitor Set'
                        : strategy.boatId
                          ? 'Specific Boat'
                          : 'All boats'}
                    </p>
                  </div>
                  <Switch
                    checked={strategy.isActive}
                    onCheckedChange={(checked) =>
                      toggleMutation.mutate({ pid: strategy.id, isActive: checked })
                    }
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="size-8 p-0 text-destructive"
                    onClick={() => setDeleteTarget(strategy)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Strategy"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This won't affect previously accepted suggestions.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}

function formatStrategyParams(type: string, params: Record<string, unknown>): string {
  switch (type) {
    case 'match_comp_avg':
      return 'Set price equal to competitor average';
    case 'undercut_pct':
      return `${params['pct'] ?? 5}% below competitor average`;
    case 'target_margin':
      return `${params['marginPct'] ?? 10}% margin over cost`;
    case 'early_bird':
      return `${params['discountPct'] ?? 15}% off, ${params['weeksOut'] ?? 12}+ weeks out`;
    case 'last_minute':
      return `${params['discountPct'] ?? 20}% off, ${params['daysOut'] ?? 14} days or less`;
    case 'demand_based':
      return `+${params['highDemandPct'] ?? 15}% high demand, -${params['lowDemandPct'] ?? 20}% low demand`;
    case 'seasonal_multiplier':
      return `Peak ×${params['peakMult'] ?? 1.15}, Shoulder ×${params['shoulderMult'] ?? 0.9}, Pre ×${params['preMult'] ?? 0.65}`;
    case 'day_of_week':
      return `Weekend +${params['weekendPremiumPct'] ?? 10}%, Midweek -${params['midweekDiscountPct'] ?? 5}%`;
    case 'event_surge':
      return `+${params['surgePct'] ?? 20}% during local events`;
    default:
      return JSON.stringify(params);
  }
}

// ── Create Strategy Dialog (multi-step) ──

function CreateStrategyDialog({ orgPid, onSuccess }: { orgPid: string; onSuccess: () => void }) {
  const [step, setStep] = useState<'type' | 'configure'>('type');
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [paramValues, setParamValues] = useState<Record<string, number>>({});
  const [priority, setPriority] = useState(0);
  const [scopeType, setScopeType] = useState<'all' | 'comp_set'>('all');
  const [compSetPid, setCompSetPid] = useState('');
  const [error, setError] = useState('');

  const def = STRATEGY_DEFS.find((d) => d.type === selectedType);

  const { data: compSetsData } = useQuery({
    queryKey: queryKeys.compSets.list(orgPid),
    queryFn: () => compSetsApi.list({ limit: 100 }),
    staleTime: 60_000,
  });

  const compSets = compSetsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: () => {
      if (!def) throw new Error('No strategy selected');
      const params: Record<string, unknown> = {};
      for (const p of def.params) {
        params[p.key] = paramValues[p.key] ?? p.defaultValue;
      }
      return pricingSuggestionsApi.createStrategy({
        name,
        strategyType: def.type,
        params,
        priority,
        ...(scopeType === 'comp_set' && compSetPid ? { compSetPid } : {}),
      });
    },
    onSuccess: () => {
      toast.success('Strategy created');
      onSuccess();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create'),
  });

  const handleSelectType = (type: string) => {
    setSelectedType(type);
    const d = STRATEGY_DEFS.find((s) => s.type === type)!;
    // Pre-fill defaults
    const defaults: Record<string, number> = {};
    for (const p of d.params) {
      defaults[p.key] = p.defaultValue;
    }
    setParamValues(defaults);
    setName('');
    setScopeType(d.requiresCompSet ? 'comp_set' : 'all');
    setCompSetPid('');
    setError('');
    setStep('configure');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give your strategy a name');
      return;
    }
    if (scopeType === 'comp_set' && !compSetPid) {
      setError('Select a competitor set');
      return;
    }
    createMutation.mutate();
  };

  return (
    <DialogContent className="sm:max-w-xl">
      {step === 'type' ? (
        <>
          <DialogHeader>
            <DialogTitle>Create Pricing Strategy</DialogTitle>
            <DialogDescription>
              A pricing strategy defines a rule that automatically generates pricing suggestions for
              your charter boats. Pick a strategy type to get started.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            {STRATEGY_DEFS.map((d) => {
              const Icon = d.icon;
              return (
                <button
                  key={d.type}
                  type="button"
                  onClick={() => handleSelectType(d.type)}
                  className="flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-muted/50 hover:border-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <div
                    className={`flex size-9 shrink-0 items-center justify-center rounded-md ${d.color.bg}`}
                  >
                    <Icon className={`size-4 ${d.color.text}`} />
                  </div>
                  <div className="flex-1 space-y-0.5">
                    <p className="text-sm font-medium">{d.label}</p>
                    <p className="text-xs text-muted-foreground">{d.shortDesc}</p>
                  </div>
                  <ChevronRight className="mt-2 size-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        </>
      ) : def ? (
        <>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStep('type')}
                className="rounded-md p-1 hover:bg-muted transition-colors"
              >
                <ArrowLeft className="size-4" />
              </button>
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <def.icon className={`size-4 ${def.color.text}`} />
                  {def.label}
                </DialogTitle>
                <DialogDescription>{def.shortDesc}</DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Explanation */}
          <div className="rounded-lg bg-muted/50 p-3 space-y-2">
            <p className="text-sm text-muted-foreground">{def.longDesc}</p>
            <div className="flex items-start gap-2 rounded-md bg-background p-2">
              <Lightbulb className="mt-0.5 size-3.5 shrink-0 text-amber-500" />
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Example:</span> {def.example}
              </p>
            </div>
          </div>

          <Separator />

          {/* Configuration Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div
                role="alert"
                className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
              >
                {error}
              </div>
            )}

            {/* Strategy Name */}
            <div className="space-y-1.5">
              <Label htmlFor="strategy-name">Strategy name</Label>
              <Input
                id="strategy-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                }}
                placeholder={`e.g. ${def.type === 'undercut_pct' ? 'Undercut Split Market 5%' : def.type === 'early_bird' ? 'Early Bird Summer 2026' : def.type === 'last_minute' ? 'Last Minute Fill' : def.type === 'target_margin' ? 'Standard 25% Margin' : 'Match Market Average'}`}
              />
              <p className="text-xs text-muted-foreground">
                A descriptive name to identify this strategy in your list.
              </p>
            </div>

            {/* Strategy Parameters */}
            {def.params.length > 0 && (
              <div
                className={`grid gap-4 ${def.params.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}
              >
                {def.params.map((p) => (
                  <div key={p.key} className="space-y-1.5">
                    <Label htmlFor={`param-${p.key}`}>{p.label}</Label>
                    <div className="relative">
                      <Input
                        id={`param-${p.key}`}
                        type="number"
                        min={p.min}
                        max={p.max}
                        step={p.step}
                        value={paramValues[p.key] ?? p.defaultValue}
                        onChange={(e) =>
                          setParamValues((prev) => ({
                            ...prev,
                            [p.key]: Number(e.target.value),
                          }))
                        }
                        className="pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                        {p.unit}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{p.hint}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Scope */}
            <div className="space-y-1.5">
              <Label>Scope</Label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setScopeType('all');
                    setCompSetPid('');
                  }}
                  className={`flex-1 rounded-lg border p-2.5 text-left text-sm transition-colors ${
                    scopeType === 'all'
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <p className="font-medium">All boats</p>
                  <p className="text-xs text-muted-foreground">Applies to your entire fleet</p>
                </button>
                <button
                  type="button"
                  onClick={() => setScopeType('comp_set')}
                  className={`flex-1 rounded-lg border p-2.5 text-left text-sm transition-colors ${
                    scopeType === 'comp_set'
                      ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <p className="font-medium">Competitor set</p>
                  <p className="text-xs text-muted-foreground">Only boats in a competitor set</p>
                </button>
              </div>
              {scopeType === 'comp_set' && (
                <Select
                  value={compSetPid || undefined}
                  onValueChange={(v) => {
                    setCompSetPid(v);
                    setError('');
                  }}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select a competitor set..." />
                  </SelectTrigger>
                  <SelectContent>
                    {compSets.map((cs: CompSet) => (
                      <SelectItem key={cs.id} value={cs.id}>
                        {cs.name} ({cs.marketBoatCount} boats)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Priority */}
            <div className="space-y-1.5">
              <Label htmlFor="strategy-priority">Priority</Label>
              <Input
                id="strategy-priority"
                type="number"
                min={0}
                max={100}
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
              />
              <p className="text-xs text-muted-foreground">
                Higher priority strategies are evaluated first. When multiple strategies apply to
                the same booking period, only the highest-priority match generates a suggestion.
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setStep('type')}
              >
                <ArrowLeft className="mr-1 size-4" />
                Back
              </Button>
              <Button type="submit" className="flex-1" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Strategy'}
              </Button>
            </div>
          </form>
        </>
      ) : null}
    </DialogContent>
  );
}

// ── Suggestions Tab ──

interface BoatSuggestionGroup {
  boatId: string;
  boatName: string;
  boatModel: string | null;
  suggestions: PricingSuggestion[];
  totalPriceDelta: number;
  pendingCount: number;
  avgConfidence: number | null;
  strategies: string[];
}

function SuggestionsTab({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('pending');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [acceptTarget, setAcceptTarget] = useState<PricingSuggestion | null>(null);
  const [pushToMmk, setPushToMmk] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.suggestions.list(orgPid, page, '', statusFilter),
    queryFn: () =>
      pricingSuggestionsApi.listSuggestions({
        page,
        limit: 50,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      }),
  });

  const generateMutation = useMutation({
    mutationFn: () => pricingSuggestionsApi.generate(),
    onSuccess: (res) => {
      toast.success(`Generated ${res.generated} suggestions`);
      void queryClient.invalidateQueries({ queryKey: queryKeys.suggestions.all });
    },
    onError: () => toast.error('Failed to generate suggestions'),
  });

  const reviewMutation = useMutation({
    mutationFn: ({
      pid,
      status,
      pushToMmk: push,
    }: {
      pid: string;
      status: 'accepted' | 'rejected';
      pushToMmk?: boolean;
    }) => pricingSuggestionsApi.review(pid, { status, ...(push ? { pushToMmk: true } : {}) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.suggestions.all });
      toast.success('Suggestion reviewed');
    },
    onError: () => toast.error('Failed to review suggestion'),
  });

  const bulkReviewMutation = useMutation({
    mutationFn: ({ pids, status }: { pids: string[]; status: 'accepted' | 'rejected' }) =>
      pricingSuggestionsApi.bulkReview({
        suggestionPids: pids,
        status,
      }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.suggestions.all });
      toast.success(`${res.reviewed} suggestions reviewed`);
      setSelected(new Set());
    },
    onError: () => toast.error('Failed to review suggestions'),
  });

  const suggestions = data?.data ?? [];
  const meta = data?.meta;

  // Group suggestions by boat
  const boatGroups = useMemo<BoatSuggestionGroup[]>(() => {
    const groupMap = new Map<string, PricingSuggestion[]>();
    for (const s of suggestions) {
      const existing = groupMap.get(s.boatId) ?? [];
      existing.push(s);
      groupMap.set(s.boatId, existing);
    }

    return Array.from(groupMap.entries()).map(([boatId, items]) => {
      items.sort((a, b) => a.dateFrom.localeCompare(b.dateFrom));
      const first = items[0]!;
      const pendingCount = items.filter((i) => i.status === 'pending').length;
      const confidences = items.map((i) => i.confidence).filter((c): c is number => c != null);

      return {
        boatId,
        boatName: first.boatName,
        boatModel: first.boatModel,
        suggestions: items,
        totalPriceDelta: items.reduce(
          (sum, i) => sum + (parseFloat(i.suggestedPrice) - parseFloat(i.currentPrice)),
          0
        ),
        pendingCount,
        avgConfidence:
          confidences.length > 0
            ? Math.round(confidences.reduce((a, b) => a + b, 0) / confidences.length)
            : null,
        strategies: [...new Set(items.map((i) => i.strategyType))],
      };
    });
  }, [suggestions]);

  const allPendingIds = suggestions.filter((s) => s.status === 'pending').map((s) => s.id);
  const allSelected = allPendingIds.length > 0 && allPendingIds.every((id) => selected.has(id));

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleBoatSelection = (ids: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allIn = ids.every((id) => next.has(id));
      if (allIn) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allPendingIds));
    }
  };

  const eurFormatter = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v);
              setPage(1);
              setSelected(new Set());
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
        >
          <RefreshCw
            className={`mr-1 size-3.5 ${generateMutation.isPending ? 'animate-spin' : ''}`}
          />
          Generate
        </Button>
      </div>

      {/* Suggestions List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : boatGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Lightbulb className="mx-auto mb-2 size-8 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground">No suggestions found.</p>
            <p className="text-muted-foreground text-xs">
              Create strategies and click "Generate" to get pricing suggestions.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Select all */}
          {statusFilter === 'pending' && allPendingIds.length > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
              <span className="text-muted-foreground text-sm">
                Select all ({allPendingIds.length})
              </span>
            </div>
          )}

          <div className="space-y-3">
            {boatGroups.map((group) => (
              <BoatGroupCard
                key={group.boatId}
                group={group}
                selectedIds={selected}
                statusFilter={statusFilter}
                eurFormatter={eurFormatter}
                onToggleSelect={toggleSelect}
                onToggleBoat={toggleBoatSelection}
                onAcceptOne={(s) => setAcceptTarget(s)}
                onRejectOne={(pid) => reviewMutation.mutate({ pid, status: 'rejected' })}
                onAcceptBoat={(pids) => bulkReviewMutation.mutate({ pids, status: 'accepted' })}
                onRejectBoat={(pids) => bulkReviewMutation.mutate({ pids, status: 'rejected' })}
              />
            ))}
          </div>

          {/* Bulk actions */}
          {selected.size > 0 && (
            <div className="sticky bottom-4 flex items-center justify-between rounded-lg border bg-background p-3 shadow-lg">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-green-600"
                  onClick={() =>
                    bulkReviewMutation.mutate({ pids: Array.from(selected), status: 'accepted' })
                  }
                  disabled={bulkReviewMutation.isPending}
                >
                  <Check className="mr-1 size-3.5" />
                  Accept All
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  onClick={() =>
                    bulkReviewMutation.mutate({ pids: Array.from(selected), status: 'rejected' })
                  }
                  disabled={bulkReviewMutation.isPending}
                >
                  <X className="mr-1 size-3.5" />
                  Reject All
                </Button>
              </div>
            </div>
          )}

          {/* Pagination */}
          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Page {meta.page} of {meta.totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="mr-1 size-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight className="ml-1 size-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Accept confirmation */}
      <Dialog
        open={!!acceptTarget}
        onOpenChange={(open) => {
          if (!open) {
            setAcceptTarget(null);
            setPushToMmk(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Accept Suggestion</DialogTitle>
            <DialogDescription>
              {acceptTarget
                ? `This will update the live pricing for ${acceptTarget.boatName} (${formatDateRange(acceptTarget.dateFrom, acceptTarget.dateTo)}) to ${eurFormatter.format(parseFloat(acceptTarget.suggestedPrice))}.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3">
              <Switch id="push-mmk" checked={pushToMmk} onCheckedChange={setPushToMmk} />
              <Label htmlFor="push-mmk" className="flex-1">
                <p className="text-sm font-medium">Push to MMK</p>
                <p className="text-xs text-muted-foreground">
                  Also update the price in MMK Booking Manager (only works for MMK-synced boats)
                </p>
              </Label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                setAcceptTarget(null);
                setPushToMmk(false);
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={() => {
                if (acceptTarget) {
                  reviewMutation.mutate({
                    pid: acceptTarget.id,
                    status: 'accepted',
                    ...(pushToMmk ? { pushToMmk: true } : {}),
                  });
                  setAcceptTarget(null);
                  setPushToMmk(false);
                }
              }}
            >
              Accept & Update Price
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Boat Group Card ──

function BoatGroupCard({
  group,
  selectedIds,
  statusFilter,
  eurFormatter,
  onToggleSelect,
  onToggleBoat,
  onAcceptOne,
  onRejectOne,
  onAcceptBoat,
  onRejectBoat,
}: {
  group: BoatSuggestionGroup;
  selectedIds: Set<string>;
  statusFilter: string;
  eurFormatter: Intl.NumberFormat;
  onToggleSelect: (id: string) => void;
  onToggleBoat: (ids: string[]) => void;
  onAcceptOne: (s: PricingSuggestion) => void;
  onRejectOne: (pid: string) => void;
  onAcceptBoat: (pids: string[]) => void;
  onRejectBoat: (pids: string[]) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);

  const boatPendingIds = group.suggestions.filter((s) => s.status === 'pending').map((s) => s.id);
  const allBoatSelected =
    boatPendingIds.length > 0 && boatPendingIds.every((id) => selectedIds.has(id));
  const someBoatSelected = boatPendingIds.some((id) => selectedIds.has(id));

  return (
    <Card>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        {/* Boat Header */}
        <div className="flex items-center gap-3 p-4 pb-3">
          {statusFilter === 'pending' && boatPendingIds.length > 0 && (
            <Checkbox
              checked={allBoatSelected ? true : someBoatSelected ? 'indeterminate' : false}
              onCheckedChange={() => onToggleBoat(boatPendingIds)}
              className="shrink-0"
            />
          )}
          <Ship className="size-5 shrink-0 text-muted-foreground" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold truncate">{group.boatName}</p>
              {group.boatModel && (
                <span className="text-sm text-muted-foreground">({group.boatModel})</span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mt-0.5">
              <span>
                {group.suggestions.length} period{group.suggestions.length !== 1 ? 's' : ''}
              </span>
              <span className="text-muted-foreground/40">·</span>
              <span
                className={
                  group.totalPriceDelta < 0
                    ? 'text-green-600'
                    : group.totalPriceDelta > 0
                      ? 'text-red-600'
                      : ''
                }
              >
                Net: {group.totalPriceDelta >= 0 ? '+' : ''}
                {eurFormatter.format(group.totalPriceDelta)}
              </span>
              {group.avgConfidence != null && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">Avg confidence: {group.avgConfidence}%</span>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        How confident the system is in these suggestions based on available
                        competitor data
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
              {group.strategies.map((st) => (
                <Badge key={st} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                  {STRATEGY_LABELS[st] ?? st}
                </Badge>
              ))}
            </div>
          </div>

          {/* Per-boat accept/reject all */}
          {statusFilter === 'pending' && group.pendingCount > 0 && (
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="size-8 p-0 text-green-600"
                title={`Accept all ${group.pendingCount} periods`}
                onClick={() => onAcceptBoat(boatPendingIds)}
              >
                <Check className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="size-8 p-0 text-destructive"
                title={`Reject all ${group.pendingCount} periods`}
                onClick={() => onRejectBoat(boatPendingIds)}
              >
                <X className="size-4" />
              </Button>
            </div>
          )}

          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="size-8 p-0 shrink-0">
              <ChevronDown
                className={`size-4 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`}
              />
            </Button>
          </CollapsibleTrigger>
        </div>

        {/* Period Rows */}
        <CollapsibleContent>
          <Separator />
          <div className="divide-y">
            {group.suggestions.map((s) => (
              <PeriodRow
                key={s.id}
                suggestion={s}
                isSelected={selectedIds.has(s.id)}
                statusFilter={statusFilter}
                eurFormatter={eurFormatter}
                onToggleSelect={() => onToggleSelect(s.id)}
                onAccept={() => onAcceptOne(s)}
                onReject={() => onRejectOne(s.id)}
              />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

// ── Period Row ──

function PeriodRow({
  suggestion: s,
  isSelected,
  statusFilter,
  eurFormatter,
  onToggleSelect,
  onAccept,
  onReject,
}: {
  suggestion: PricingSuggestion;
  isSelected: boolean;
  statusFilter: string;
  eurFormatter: Intl.NumberFormat;
  onToggleSelect: () => void;
  onAccept: () => void;
  onReject: () => void;
}) {
  const [showDetails, setShowDetails] = useState(false);

  const current = parseFloat(s.currentPrice);
  const suggested = parseFloat(s.suggestedPrice);
  const def = STRATEGY_DEFS.find((d) => d.type === s.strategyType);
  const StrategyIcon =
    def?.icon ??
    (s.strategyType === 'last_minute_deal'
      ? Zap
      : s.strategyType === 'orphan_gap'
        ? Clock
        : Settings2);
  const hasMarketData = s.compSetAvgPrice || s.compSetMinPrice || s.compSetMaxPrice;

  return (
    <div className="px-4 py-2.5 hover:bg-muted/30 transition-colors">
      {/* Main row */}
      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
        {/* Checkbox */}
        {statusFilter === 'pending' && s.status === 'pending' && (
          <Checkbox checked={isSelected} onCheckedChange={onToggleSelect} className="shrink-0" />
        )}

        {/* Date range + product */}
        <div className="w-full sm:w-36 shrink-0">
          <p className="text-sm font-medium">{formatDateRange(s.dateFrom, s.dateTo)}</p>
          <p className="text-xs text-muted-foreground">{s.product}</p>
        </div>

        {/* Price change */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="font-mono text-sm text-muted-foreground">
            {eurFormatter.format(current)}
          </span>
          <ArrowRight className="size-3 text-muted-foreground shrink-0" />
          <span className="font-mono text-sm font-semibold">{eurFormatter.format(suggested)}</span>
          <PriceChangeBadge currentPrice={current} suggestedPrice={suggested} />
        </div>

        {/* Strategy + confidence */}
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <StrategyIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="text-xs text-muted-foreground truncate hidden sm:inline">
            {def?.label ?? STRATEGY_LABELS[s.strategyType] ?? s.strategyType}
          </span>
          {s.confidence != null && <ConfidenceBadge confidence={s.confidence} />}
        </div>

        {/* Type badges */}
        <div className="flex items-center gap-1 shrink-0">
          {s.strategyType === 'last_minute_deal' && (
            <Badge className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 text-[10px]">
              <Zap className="mr-0.5 size-3" />
              Deal
            </Badge>
          )}
          {s.strategyType === 'orphan_gap' && (
            <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 text-[10px]">
              Gap
            </Badge>
          )}
          {statusFilter !== 'pending' && <StatusBadge status={s.status} />}
        </div>

        {/* Expand details */}
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-muted-foreground shrink-0"
          onClick={() => setShowDetails(!showDetails)}
        >
          <ChevronDown
            className={`size-3.5 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
          />
        </Button>

        {/* Per-period accept/reject */}
        {s.status === 'pending' && (
          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="size-7 p-0 text-green-600"
              onClick={onAccept}
            >
              <Check className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="size-7 p-0 text-destructive"
              onClick={onReject}
            >
              <X className="size-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Expandable detail panel */}
      {showDetails && (
        <div className="mt-2 ml-0 sm:ml-9 space-y-1.5 text-xs">
          {s.reasoning && (
            <div className="flex items-start gap-2 rounded-md bg-muted/50 px-2.5 py-2">
              <StrategyIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              <p className="leading-relaxed">
                <span className="font-medium">
                  {def?.label ?? STRATEGY_LABELS[s.strategyType] ?? s.strategyType}
                </span>
                <span className="text-muted-foreground"> — {s.reasoning}</span>
              </p>
            </div>
          )}
          {s.signals && s.signals.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {s.signals.map((signal, i) => (
                <Badge
                  key={i}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0 h-5 font-normal"
                >
                  {signal}
                </Badge>
              ))}
            </div>
          )}
          {hasMarketData && (
            <div className="flex items-center gap-3 text-muted-foreground">
              <span className="font-medium uppercase tracking-wide">Market:</span>
              {s.compSetAvgPrice && (
                <span>Avg {eurFormatter.format(parseFloat(s.compSetAvgPrice))}</span>
              )}
              {s.compSetMinPrice && (
                <span>Min {eurFormatter.format(parseFloat(s.compSetMinPrice))}</span>
              )}
              {s.compSetMaxPrice && (
                <span>Max {eurFormatter.format(parseFloat(s.compSetMaxPrice))}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const color =
    confidence >= 80
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : confidence >= 50
        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';

  const label = confidence >= 80 ? 'High' : confidence >= 50 ? 'Medium' : 'Low';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="secondary" className={`text-[10px] shrink-0 cursor-help ${color}`}>
            {confidence}%
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top">
          {label} confidence — based on how much competitor data is available
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    accepted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    expired: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };

  return (
    <Badge variant="secondary" className={`text-xs ${variants[status] ?? ''}`}>
      {status}
    </Badge>
  );
}

// ── Events Tab ──

const EVENT_CATEGORIES = [
  { value: 'regatta', label: 'Regatta' },
  { value: 'festival', label: 'Festival' },
  { value: 'concert', label: 'Concert' },
  { value: 'sport', label: 'Sport' },
  { value: 'holiday', label: 'Holiday' },
  { value: 'other', label: 'Other' },
];

function EventsTab({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LocalEvent | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.localEvents.list(orgPid),
    queryFn: () => localEventsApi.list({ limit: 100 }),
  });

  const deleteMutation = useMutation({
    mutationFn: (pid: string) => localEventsApi.remove(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.localEvents.list(orgPid) });
      toast.success('Event deleted');
    },
    onError: () => toast.error('Failed to delete event'),
  });

  const events = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Local events drive demand. Add regattas, festivals, and other events so the Event Surge
            strategy can adjust pricing automatically.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-1 size-4" />
              Add Event
            </Button>
          </DialogTrigger>
          <CreateEventDialog
            onSuccess={() => {
              setCreateOpen(false);
              void queryClient.invalidateQueries({ queryKey: queryKeys.localEvents.list(orgPid) });
            }}
          />
        </Dialog>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Sparkles className="mx-auto mb-2 size-8 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground font-medium">No events yet</p>
            <p className="text-muted-foreground mt-1 max-w-sm mx-auto text-xs">
              Add local events like regattas, festivals, or concerts. The Event Surge strategy uses
              these to automatically increase prices during high-demand periods.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {events.map((event) => (
            <Card key={event.id}>
              <CardContent className="flex items-center gap-4 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <MapPin className="size-5 text-muted-foreground" />
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{event.name}</p>
                    <Badge variant="outline" className="text-xs capitalize">
                      {event.category}
                    </Badge>
                    {event.impactRadius && event.impactRadius !== 'local' && (
                      <Badge variant="secondary" className="text-xs capitalize">
                        {event.impactRadius}
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {event.location} · {formatDateRange(event.dateFrom, event.dateTo)}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="size-8 p-0 text-destructive"
                  onClick={() => setDeleteTarget(event)}
                >
                  <Trash2 className="size-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Event"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This won't affect existing pricing suggestions.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => {
          if (deleteTarget) {
            deleteMutation.mutate(deleteTarget.id);
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
}

function CreateEventDialog({ onSuccess }: { onSuccess: () => void }) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('other');
  const [impactRadius, setImpactRadius] = useState('local');
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      localEventsApi.create({
        name,
        location,
        dateFrom,
        dateTo,
        category,
        impactRadius,
      }),
    onSuccess: () => {
      toast.success('Event created');
      onSuccess();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed to create'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Event name is required');
      return;
    }
    if (!location.trim()) {
      setError('Location is required');
      return;
    }
    if (!dateFrom || !dateTo) {
      setError('Date range is required');
      return;
    }
    if (dateFrom > dateTo) {
      setError('Start date must be before end date');
      return;
    }
    createMutation.mutate();
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Add Local Event</DialogTitle>
        <DialogDescription>
          Add an event that may affect charter demand in your area.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-1.5">
          <Label htmlFor="event-name">Event name</Label>
          <Input
            id="event-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError('');
            }}
            placeholder="e.g. Split Regatta 2026"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="event-location">Location</Label>
          <Input
            id="event-location"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setError('');
            }}
            placeholder="e.g. Split, Croatia"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="event-from">Start date</Label>
            <Input
              id="event-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setError('');
              }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-to">End date</Label>
            <Input
              id="event-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setError('');
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="event-category">Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="event-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EVENT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="event-radius">Impact radius</Label>
            <Select value={impactRadius} onValueChange={setImpactRadius}>
              <SelectTrigger id="event-radius">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="local">Local</SelectItem>
                <SelectItem value="regional">Regional</SelectItem>
                <SelectItem value="national">National</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button type="submit" className="w-full" disabled={createMutation.isPending}>
          {createMutation.isPending ? 'Creating...' : 'Add Event'}
        </Button>
      </form>
    </DialogContent>
  );
}

function formatDateRange(from: string, to: string): string {
  const f = new Date(from);
  const t = new Date(to);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${f.toLocaleDateString('en', opts)} – ${t.toLocaleDateString('en', opts)}`;
}
